import { useEffect, useMemo } from 'react';

import { demoGameUrlRequest, gameUrlRequest } from 'api/casino';
import { AuthStatus, useClientProfile, useLanguage } from 'store';
import { base64ToUrl } from 'utils/casino';
import type { LoadingState } from 'utils/types';
import useLazyRequest from 'utils/useLazyRequest';

type UseCasinoGame = (
  gameId: number,
  isDemo?: boolean,
  providerName?: string,
) => {
  loading: LoadingState;
  gameUrl?: string;
  gameData?: Awaited<ReturnType<typeof gameUrlRequest>>['game'];
  error?: Error;
};

const useCasinoGame: UseCasinoGame = (gameId, isDemo, providerName) => {
  const [{ status, sessionToken }] = useClientProfile();
  const { data, loading, error, execute } = useLazyRequest(
    isDemo ? demoGameUrlRequest : gameUrlRequest,
  );
  const [{ current: language }] = useLanguage();

  useEffect(() => {
    const isAuthResolved = status !== AuthStatus.resolving;
    const isSignedIn = status === AuthStatus.signedIn;

    if (!isAuthResolved) {
      return;
    }

    if (isDemo || isSignedIn) {
      execute({
        query: {
          game: gameId,
          locale: language.code,
          providerName,
        },
        headers: {
          'x-locale': language.code,
          ...(isSignedIn && { authorization: `Bearer ${sessionToken}` }),
        },
      });
    }
  }, [
    execute,
    isDemo,
    providerName,
    status,
    sessionToken,
    gameId,
    language.code,
  ]);

  const gameUrl = useMemo(() => {
    if (data) {
      const { type, link } = data.data;
      if (type === 'BASE64') {
        return base64ToUrl(link);
      }

      return link;
    }

    return 'about:blank';
  }, [data]);

  return {
    loading,
    gameUrl,
    gameData: data?.game,
    error,
  };
};

export default useCasinoGame;
